/**
 * The legacy wrench mark: the white wrench glyph the legacy app shipped
 * (`public/assets/facilities/svg-assets/bx_wrench.svg`) in a disc of a risk
 * colour, ringed by a 30% wash of the same colour. The inspection PDF opens an
 * outcome with it, and the floorplan's Live view marks a leg's damage awaiting
 * repair with it — the symbol legacy used for inspection outcomes, kept apart
 * from the plan's coloured device marks, which say what a sensor reports.
 *
 * The server composes the same mark (`app/services/wrench_mark.rb`) from the
 * file; the canvas cannot read the file, so the glyph's path is copied here and
 * `__tests__/wrench-mark.test.ts` holds the copy equal to the file.
 *
 * Plain data and pure helpers: the renderers, the legend and the PDF's legend
 * read it, so none of them can drift.
 */
import { type Graphics, GraphicsPath, Matrix } from "pixi.js"

/** The glyph's path, in its own 12.5-unit box (`bx_wrench.svg`). */
export const WRENCH_GLYPH_PATH =
  "M 3.025 12.5 C 2.848 12.5 2.679 12.43 2.554 12.305 L 0.196 9.949 C 0.113 9.867 0.053 9.764 0.023 9.651 C -0.008 9.538 -0.008 9.418 0.023 9.305 C 0.053 9.192 0.113 9.089 0.196 9.007 L 2.727 6.477 C 2.576 5.998 2.5 5.498 2.502 4.996 C 2.501 4.34 2.63 3.69 2.882 3.083 C 3.133 2.477 3.503 1.927 3.968 1.464 C 4.429 0.999 4.977 0.631 5.581 0.38 C 6.185 0.128 6.833 -0.001 7.487 0 L 7.501 0 C 8.153 0 8.798 0.127 9.401 0.375 C 9.537 0.431 9.651 0.53 9.725 0.657 C 9.799 0.784 9.828 0.932 9.809 1.078 C 9.79 1.224 9.723 1.359 9.619 1.463 L 7.502 3.584 L 8.916 4.999 L 11.039 2.878 C 11.143 2.775 11.277 2.708 11.422 2.689 C 11.568 2.67 11.715 2.699 11.842 2.772 C 11.969 2.844 12.068 2.957 12.125 3.092 C 12.377 3.695 12.505 4.343 12.503 4.997 C 12.504 5.653 12.375 6.304 12.123 6.91 C 11.872 7.516 11.502 8.067 11.037 8.53 C 10.576 8.994 10.027 9.363 9.423 9.614 C 8.819 9.865 8.17 9.994 7.516 9.993 L 7.496 9.993 C 6.985 9.994 6.477 9.915 5.99 9.761 L 6.025 9.771 L 3.496 12.301 C 3.371 12.426 3.202 12.496 3.025 12.496 Z M 1.611 9.476 L 3.026 10.891 L 5.383 8.534 C 5.444 8.471 5.518 8.421 5.598 8.387 C 5.679 8.352 5.766 8.334 5.854 8.334 C 5.943 8.334 6.03 8.351 6.112 8.386 C 6.783 8.655 7.518 8.723 8.226 8.581 C 8.935 8.439 9.587 8.093 10.102 7.586 C 10.443 7.246 10.713 6.843 10.898 6.398 C 11.082 5.954 11.177 5.477 11.176 4.996 C 11.176 4.872 11.17 4.749 11.158 4.628 L 11.158 4.649 L 9.39 6.413 C 9.307 6.496 9.204 6.555 9.091 6.585 C 8.978 6.616 8.86 6.616 8.747 6.585 C 8.634 6.555 8.531 6.496 8.448 6.413 L 6.091 4.056 C 6.008 3.973 5.949 3.87 5.919 3.757 C 5.888 3.644 5.888 3.526 5.919 3.413 C 5.949 3.3 6.008 3.197 6.091 3.114 L 7.852 1.349 C 7.752 1.339 7.627 1.333 7.505 1.333 C 7.024 1.333 6.547 1.428 6.103 1.612 C 5.658 1.796 5.254 2.066 4.914 2.407 C 4.398 2.921 4.047 3.578 3.907 4.293 C 3.766 5.008 3.842 5.749 4.125 6.42 L 4.116 6.396 C 4.15 6.476 4.167 6.563 4.166 6.65 C 4.166 6.738 4.148 6.825 4.113 6.906 C 4.079 6.986 4.029 7.06 3.966 7.121 L 1.612 9.478 Z"

/** The glyph's box: the file's 12-unit viewBox, which its strokes overrun. */
export const WRENCH_GLYPH_BOX = 12.5

/** The wash ring's opacity, and the solid disc's share of the mark's radius. */
export const WRENCH_RING_ALPHA = 0.3
export const WRENCH_DISC_SHARE = 0.66
/** How wide the glyph is, as a share of the mark's diameter. */
export const WRENCH_GLYPH_SHARE = 0.44

/**
 * The mark as an SVG document, `size` px square — for the page's legend, which
 * draws with HTML. Same proportions as the server's (`WrenchMark.svg`).
 */
export function wrenchMarkSvg(colour: string, size: number): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 100 100" aria-hidden="true">` +
    `<circle cx="50" cy="50" r="50" fill="${colour}" fill-opacity="${WRENCH_RING_ALPHA}"/>` +
    `<circle cx="50" cy="50" r="33" fill="${colour}"/>` +
    `<svg x="28" y="28" width="44" height="44" viewBox="-0.15 -0.15 12.8 12.8">` +
    `<path d="${WRENCH_GLYPH_PATH}" fill="#ffffff"/></svg></svg>`
  )
}

/**
 * Draw the mark centred on (`x`, `y`) with outer radius `radius`, in
 * `colour`: the wash ring, the solid disc, and the white glyph on it.
 */
export function drawWrenchMark(
  graphics: Graphics,
  x: number,
  y: number,
  radius: number,
  colour: number,
): void {
  graphics.circle(x, y, radius)
  graphics.fill({ color: colour, alpha: WRENCH_RING_ALPHA })
  graphics.circle(x, y, radius * WRENCH_DISC_SHARE)
  graphics.fill({ color: colour, alpha: 1 })

  const width = radius * 2 * WRENCH_GLYPH_SHARE
  const scale = width / WRENCH_GLYPH_BOX
  const glyph = new GraphicsPath().addPath(
    new GraphicsPath(WRENCH_GLYPH_PATH),
    new Matrix(scale, 0, 0, scale, x - width / 2, y - width / 2),
  )
  graphics.path(glyph)
  graphics.fill({ color: 0xffffff, alpha: 1 })
}
